import moment, { tz } from 'moment-timezone';
import getTimeZonesArray from './timezones-array';

export const getCurrentTimestamp = (): number => Math.floor(Date.now() / 1000);

export type Timezone = {
  name: string;
  value: string;
};

export const getTimezoneList = (): Timezone[] => {
  const timeZones = getTimeZonesArray();
  const timezoneList = [];
  timeZones.forEach((timezone) => {
    const name = timezone;
    const value = `${timezone} (GMT ${tz(timezone).format('Z')})`;
    timezoneList.push({ name, value });
  });
  return timezoneList;
};

export const getTimezoneValueByName = (name: string): string => {
  const timeZoneList = getTimezoneList();
  let timeZoneValue = 'UTC';
  timeZoneList.forEach((timeZoneObj) => {
    if (timeZoneObj.name === name) {
      timeZoneValue = timeZoneObj.value;
    }
  });
  return timeZoneValue;
};

export const getCurrentTimeZone = () => {
  let timeZone = tz.guess();
  if (
    timeZone === 'Asia/Calcutta' ||
    timeZone === 'Asia/calcutta' ||
    timeZone === 'Asia/kolkata'
  ) {
    timeZone = 'Asia/Kolkata';
  }
  return timeZone;
};

export const years = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const addDatePrefix = (luxonFormattedString: string) => {
  const datePart = +luxonFormattedString.substr(
    0,
    luxonFormattedString.indexOf(' '),
  );
  const monthAndYearPart = luxonFormattedString.substr(
    luxonFormattedString.indexOf(' ') + 1,
  );

  let suffix;

  if (datePart === 1 || datePart === 21 || datePart === 31) {
    suffix = 'st';
  } else if (datePart === 2 || datePart === 22) {
    suffix = 'nd';
  } else if (datePart === 3 || datePart === 23) {
    suffix = 'rd';
  } else {
    suffix = 'th';
  }
  return `${datePart}${suffix} ${monthAndYearPart}`;
};

export const formateDateWithTime = (date?: string) =>
  date ? moment(date).format('ddd, MMM D, YYYY · h:mm A') : '';

export const formateDate = (date, format = 'DD MMM, yyyy') =>
  moment(date).format(format);

export const formatDateTimeWithUserTimezone = (
  isoDate: string,
  userTimeZone?: string,
) => {
  if (!isoDate) return '';

  // Validate timezone and fallback to current timezone if invalid
  const isValidTimezone = userTimeZone && tz.zone(userTimeZone) !== null;
  const timeZone = isValidTimezone ? userTimeZone : getCurrentTimeZone();

  const dateMoment = moment(isoDate).tz(timeZone);
  // Ensure we have a valid moment object before calling format
  if (!dateMoment.isValid()) {
    return '';
  }
  const time = dateMoment.format('h:mm A');

  // Get GMT offset using your existing timezone list function
  const timezoneList = getTimezoneList();
  const timeZoneObj = timezoneList.find((t) => t.name === timeZone);

  // Example result: "Asia/Kolkata (GMT +05:30)"
  const gmtOffset =
    timeZoneObj?.value.match(/\(GMT ([^)]+)\)/)?.[1] || '+00:00';

  return `${time} (GMT${gmtOffset}) ${timeZone}`;
};

export const formatNoteTimestamp = (
  isoDate: string,
  isCreated?: boolean,
  userTimeZone?: string,
) => {
  if (!isoDate) return '';

  // Validate timezone and fallback to current timezone if invalid
  const isValidTimezone = userTimeZone && tz.zone(userTimeZone) !== null;
  const timeZone = isValidTimezone ? userTimeZone : getCurrentTimeZone();

  const date = moment(isoDate).tz(timeZone);
  const today = moment().tz(timeZone);

  // Ensure we have valid moment objects before calling methods
  if (!date.isValid() || !today.isValid()) {
    return '';
  }

  const timezoneList = getTimezoneList();
  const timeZoneObj = timezoneList.find((t) => t.name === timeZone);
  const gmtOffset =
    timeZoneObj?.value.match(/\(GMT ([^)]+)\)/)?.[1] || '+00:00';
  const time = date.format('ddd, MMM D, YYYY [at] h:mm A');

  if (isCreated) {
    return `${time} (GMT${gmtOffset}) ${timeZone}`;
  }
  const isToday = date.isSame(today, 'day');

  if (isToday) {
    return `Today · ${date.format('h:mm A')}`;
  }

  // SHORT weekday version: 'ddd' (Mon, Tue, Wed)
  return date.format('ddd, MMM D, YYYY · h:mm A');
};
